import { BigNum, QUOTE_PRECISION_EXP, SpotMarketConfig } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import { useEffect } from 'react';
import { SPOT_MARKETS_LOOKUP } from 'src/environmentVariables/EnvironmentVariables';
import useWalletBalancesStore from 'src/stores/useWalletBalancesStore';
import useCurrentWalletCollateralBalance from './useCurrentWalletCollateralBalance';
import useGetOraclePriceForMarket from './useGetOraclePriceForMarket';
import { singletonHook } from 'react-singleton-hook';
import useInterval from './useInterval';

// use 10 minute intervals for now
const WALLET_VALUE_UPDATE_INTERVAL = 1000 * 60 * 10;

const useSyncSpotMarketBalance = (spotMarketConfig: SpotMarketConfig) => {
	const setWalletBalancesStore = useWalletBalancesStore((s) => s.set);
	const [baseBalanceBigNum, loading] =
		useCurrentWalletCollateralBalance(spotMarketConfig);

	const marketId = MarketId.createSpotMarket(spotMarketConfig.marketIndex);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();

	const updateBalances = (baseBalance: BigNum) => {
		const priceToUse = getOraclePriceForMarket(marketId);

		setWalletBalancesStore((s) => {
			const quoteValue = baseBalance
				.mul(priceToUse)
				.shiftTo(QUOTE_PRECISION_EXP);
			s.balances[spotMarketConfig.symbol] = {
				base: baseBalance,
				quote: quoteValue,
				loading,
			};
		});
	};

	// only need an update when the wallet balance changes, or on a large time interval, rather than on every price movement
	// large time interval should account for major price movements of wallet collateral
	useEffect(() => {
		updateBalances(baseBalanceBigNum);
	}, [baseBalanceBigNum?.toString(), marketId.key]);

	useInterval(
		() => updateBalances(baseBalanceBigNum),
		WALLET_VALUE_UPDATE_INTERVAL
	);
};

export const useSyncWalletBalances = singletonHook(
	() => {},
	() => {
		SPOT_MARKETS_LOOKUP.map(useSyncSpotMarketBalance);
	}
);
